import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Button, buttonVariants } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { FilterBar, FilterSelect } from '../../components/ui/filter-bar'
import { SearchInput } from '../../components/ui/search-input'
import { EmptyState, ErrorState, Loading, PageHeading } from '../components/chrome'
import { CompanyCounts, CompanyInitials, OrganizationStatus } from '../components/company-parts'
import { useI18n } from '../lib/i18n'
import { useCompanies, type Company, type CompanyStatus } from '../lib/screens'
import { foldForSearch, matchesSearch, SEARCH_MAX } from '../lib/search'

const STATUSES = ['active', 'suspended'] as const satisfies readonly CompanyStatus[]

/*
 * «Empresas» (PRD 04 §2.2, ADR 0008 §3; Figma 86:2451): every company not offboarded, by name,
 * as cards with its initials, name, state and counts, and «Ver detalle». The search (by name)
 * and the state filter happen in the page: nothing typed is sent, kept in the address or audited.
 * «+ Añadir empresa» opens the company wizard (ADR 0009 §7), also from the empty list. Without
 * the Figma's sector, «Editar» and «Eliminar», which are not built yet. Each view leaves
 * `backoffice.viewed` in Eklevo's audit trail. Marked for design review.
 */
export function CompaniesPage() {
  const { t, locale } = useI18n()
  const list = useCompanies()
  const [text, setText] = useState('')
  const [status, setStatus] = useState<CompanyStatus | ''>('')

  const wanted = foldForSearch(text, locale)
  const filtered = Boolean(wanted || status)
  const companies =
    list.data?.companies.filter(
      (c) => (!status || c.status === status) && matchesSearch(c.name, wanted, locale),
    ) ?? []
  const clear = () => {
    setText('')
    setStatus('')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading title={t('companies.title')}>{t('companies.body')}</PageHeading>
        <Link to="/empresas/nueva" className={buttonVariants()}>
          <Plus aria-hidden className="size-4" />
          {t('companies.add')}
        </Link>
      </div>
      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : list.data.companies.length === 0 ? (
        <EmptyState title={t('companies.empty.title')}>{t('companies.empty.body')}</EmptyState>
      ) : (
        <>
          <FilterBar label={t('companies.filters.label')}>
            <SearchInput
              value={text}
              onChange={setText}
              label={t('companies.search')}
              placeholder={t('companies.search')}
              maxLength={SEARCH_MAX}
            />
            <FilterSelect
              label={t('companies.filter.status')}
              value={status}
              onChange={(e) => setStatus(e.target.value as CompanyStatus | '')}
            >
              <option value="">{t('companies.filter.allStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`status.organization.${s}`)}
                </option>
              ))}
            </FilterSelect>
            {filtered && (
              <Button variant="ghost" size="sm" onClick={clear}>
                {t('common.filters.clear')}
              </Button>
            )}
          </FilterBar>
          <p role="status" className="text-sm text-ink-muted">
            {t('companies.count', { count: companies.length })}
          </p>
          {companies.length === 0 ? (
            <EmptyState title={t('companies.noResults')}>
              <Button variant="secondary" size="sm" onClick={clear}>
                {t('common.filters.clear')}
              </Button>
            </EmptyState>
          ) : (
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {companies.map((c) => (
                <li key={c.id}>
                  <CompanyCard company={c} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function CompanyCard({ company }: { company: Company }) {
  const { t } = useI18n()
  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start gap-3">
        <CompanyInitials name={company.name} />
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-medium [overflow-wrap:anywhere]">{company.name}</h2>
          <div className="mt-1">
            <OrganizationStatus status={company.status} />
          </div>
        </div>
      </div>
      <CompanyCounts company={company} className="mt-5" />
      <div className="mt-auto pt-5">
        <Link
          to="/empresas/$organizationId"
          params={{ organizationId: company.id }}
          aria-label={t('companies.openLabel', { company: company.name })}
          className="inline-flex items-center justify-center rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium hover:bg-black/5"
        >
          {t('companies.open')}
        </Link>
      </div>
    </Card>
  )
}
